// src/components/payment/PaymentPage.js
import React, { useState } from 'react';
import { Container, Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import CheckoutForm from './CheckoutForm';

// Initialize Stripe (replace with your publishable key)
const stripePromise = loadStripe('pk_test_51RrVXw1sliwK2Fk6B60lF6mPsZmmJjq6beZ3lNjzQfxRxFyUMH125wJtO2gs6GEsYrKy9lydic1D6X10w3VZRrud00QQ1V0mdb');

const PaymentPage = ({ user }) => {
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const selectedPlan = location.state;

  // Debug log to check what data we're receiving
  console.log('Selected plan from location.state:', selectedPlan);

  if (!selectedPlan) {
    return <Navigate to="/pricing" replace />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Validate plan data
  if (!selectedPlan.price || !selectedPlan.planName) {
    console.error('Invalid plan data:', selectedPlan);
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <h5>Invalid Plan Data</h5>
          <p>The selected plan data is incomplete. Please go back and select a plan again.</p>
          <Button variant="primary" onClick={() => navigate('/pricing')}>
            Go to Pricing
          </Button>
        </Alert>
      </Container>
    );
  }

  const handleGoBack = () => {
    navigate(-1);
  };

  const handlePaymentSuccess = (subscription) => {
    console.log('Payment successful:', subscription);
    setSubscriptionData(subscription);
    setPaymentStatus('success');
    setErrorMessage('');
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setPaymentStatus('error');
    setErrorMessage(error.message || 'An error occurred during payment processing');
  };

  const resetPaymentState = () => {
    setPaymentStatus(null);
    setErrorMessage('');
    setSubscriptionData(null);
  };

  return (
    <Container className="py-5">
      <Row className="mb-3">
        <Col>
          <Button 
            variant="outline-secondary" 
            onClick={handleGoBack}
            className="d-flex align-items-center"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back
          </Button>
        </Col>
      </Row>

      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow">
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">Subscribe to Sked Guy Premium</h4>
            </Card.Header>
            <Card.Body>
              {/* Success State */}
              {paymentStatus === 'success' && (
                <Alert variant="success">
                  <div className="text-center">
                    <FontAwesomeIcon icon={faCheck} size="3x" className="text-success mb-3" />
                    <h5>Subscription Successful! 🎉</h5>
                    <p>Thank you for subscribing to Sked Guy Premium. Your subscription is now active!</p>
                    
                    {subscriptionData && (
                      <div className="mt-3 p-3 bg-light rounded">
                        <h6>Subscription Details:</h6>
                        <p className="mb-1"><strong>Plan:</strong> {selectedPlan.planName}</p>
                        <p className="mb-1"><strong>Amount:</strong> ${selectedPlan.price}/month</p>
                        <p className="mb-0"><strong>Status:</strong> 
                          <span className="text-success ms-1">
                            {subscriptionData.status === 'trialing' ? 'Free Trial Active' : 'Active'}
                          </span>
                        </p>
                      </div>
                    )}
                    
                    <div className="mt-4">
                      <Button 
                        variant="primary" 
                        onClick={() => navigate('/dashboard')}
                        className="me-2"
                      >
                        Go to Dashboard
                      </Button>
                      <Button 
                        variant="outline-secondary" 
                        onClick={() => navigate('/pricing')}
                      >
                        View Plans
                      </Button>
                    </div>
                  </div>
                </Alert>
              )}

              {/* Error State */}
              {paymentStatus === 'error' && (
                <Alert variant="danger">
                  <h5>Subscription Failed</h5>
                  <p>{errorMessage}</p>
                  <Button 
                    variant="outline-primary" 
                    onClick={resetPaymentState}
                    className="mt-2"
                  >
                    Try Again
                  </Button>
                </Alert>
              )}

              {/* Payment Form */}
              {paymentStatus !== 'success' && (
                <>
                  <div className="mb-4">
                    <h5 className="mb-3">Selected Plan</h5>
                    <div className="p-3 border rounded bg-light">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-1">{selectedPlan.planName} Plan</h6>
                          <p className="mb-0 text-muted">
                            <strong>${selectedPlan.price || 'N/A'}/month</strong>
                          </p>
                        </div>
                        <div className="text-end">
                          {selectedPlan.trialPeriodDays && selectedPlan.trialPeriodDays > 0 && (
                            <small className="text-success">
                              {selectedPlan.trialPeriodDays} days free trial
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <ul className="list-unstyled mt-3">
                      <li className="mb-2">✅ {selectedPlan.rows || 'Unlimited schedules'}</li>
                      <li className="mb-2">✅ {selectedPlan.timeframe || 'Advanced scheduling'}</li>
                      <li className="mb-2">✅ {selectedPlan.access || 'Priority support'}</li>
                    </ul>
                  </div>

                  <Elements stripe={stripePromise}>
                    <CheckoutForm 
                      user={user}
                      selectedPlan={selectedPlan}
                      onPaymentSuccess={handlePaymentSuccess}
                      onPaymentError={handlePaymentError}
                    />
                  </Elements>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PaymentPage;